import { AlertCircle, TrendingUp } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { Spinner } from '@/components/ui';
import { useLoanCashflows, useLoanNPV, useLoanYield } from '@/hooks/use-cashflows';
import {
  fmtCurrencyExact,
  formatAnnualBps,
  formatAnnualPercent,
  formatRatioPercent,
  formatYears,
} from '@/lib/format';

function parseCashflowDate(value: string): Date | null {
  const monthMatch = /^(\d{4})-(\d{2})$/.exec(value);
  if (monthMatch) {
    const year = Number(monthMatch[1]);
    const monthIndex = Number(monthMatch[2]) - 1;

    if (monthIndex >= 0 && monthIndex <= 11) {
      return new Date(year, monthIndex, 1);
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function formatCashflowDate(value: string): string {
  const parsed = parseCashflowDate(value);
  const isMonthOnly = /^\d{4}-\d{2}$/.test(value);

  if (!parsed) {
    return value;
  }

  if (isMonthOnly) {
    return parsed.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
    });
  }

  return parsed.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface NPVSummaryProps {
  loanId: string;
}

function NPVSummary({ loanId }: NPVSummaryProps) {
  const [searchParams] = useSearchParams();
  const { data: npv, isLoading, error } = useLoanNPV(loanId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="lg" className="w-5 h-5 text-gray-400 animate-spin mr-2" />
        <span className="text-sm text-gray-400">Loading NPV…</span>
      </div>
    );
  }

  if (error || !npv) {
    return (
      <div className="flex items-center justify-center py-8 text-red-600">
        <AlertCircle className="w-5 h-5 mr-2" />
        <span className="text-sm">Error loading NPV</span>
      </div>
    );
  }

  const hasDiscountRate = npv.discountRate !== null && npv.discountRate !== undefined;
  const hasFutureCashflows =
    Number(npv.cashflowCount ?? 0) > 0 && Number(npv.totalFutureCashflows ?? 0) > 0;
  const safeNpv = {
    npv: npv.npv !== null && npv.npv !== undefined ? Number(npv.npv) : null,
    discountRate: hasDiscountRate ? Number(npv.discountRate) : null,
    wal: Number(npv.wal ?? 0),
    npvToParRatio:
      npv.npvToParRatio !== null && npv.npvToParRatio !== undefined
        ? Number(npv.npvToParRatio)
        : null,
  };
  const fundingTabParams = new URLSearchParams(searchParams);
  fundingTabParams.set('tab', 'funding');

  return (
    <>
      {!hasDiscountRate && hasFutureCashflows && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-xs text-amber-900">
            NPV is unavailable because this loan has no funding allocation yet. Add funding to this
            loan, and the system will use the allocated funding cost rate to calculate the discount
            rate and NPV.
          </p>
          <Link
            to={`?${fundingTabParams.toString()}`}
            className="mt-2 inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-700"
          >
            Manage Funding
          </Link>
        </div>
      )}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 mb-2">NPV</p>
          <p className="text-xl font-semibold text-gray-900">
            {safeNpv.npv !== null ? fmtCurrencyExact(safeNpv.npv) : 'N/A'}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 mb-2">Discount Rate</p>
          <p className="text-xl font-semibold text-gray-900">
            {safeNpv.discountRate !== null ? formatAnnualPercent(safeNpv.discountRate) : 'Not set'}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 mb-2">WAL</p>
          <p className="text-xl font-semibold text-gray-900">{formatYears(safeNpv.wal, 2)}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 mb-2">NPV-to-Par Ratio</p>
          <p className="text-xl font-semibold text-gray-900">
            {safeNpv.npvToParRatio !== null ? formatRatioPercent(safeNpv.npvToParRatio) : 'N/A'}
          </p>
        </div>
      </div>
    </>
  );
}

interface CashflowTableProps {
  loanId: string;
}

function CashflowTable({ loanId }: CashflowTableProps) {
  const { data: cashflowsRaw, isLoading, error } = useLoanCashflows(loanId);
  const now = new Date();
  const cashflows = Array.isArray(cashflowsRaw) ? cashflowsRaw : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" className="w-5 h-5 text-gray-400 animate-spin mr-2" />
        <span className="text-sm text-gray-400">Loading cashflows…</span>
      </div>
    );
  }

  if (error || !cashflows) {
    return (
      <div className="flex items-center justify-center py-12 text-red-600">
        <AlertCircle className="w-5 h-5 mr-2" />
        <span className="text-sm">Error loading cashflows</span>
      </div>
    );
  }

  if (cashflows.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <span className="text-sm">No cashflow data available</span>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-700">Date</th>
            <th className="px-4 py-3 text-right font-medium text-gray-700">Principal</th>
            <th className="px-4 py-3 text-right font-medium text-gray-700">Interest</th>
            <th className="px-4 py-3 text-right font-medium text-gray-700">Fees</th>
            <th className="px-4 py-3 text-right font-medium text-gray-700">Total</th>
            <th className="px-4 py-3 text-right font-medium text-gray-700">Cumulative</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {cashflows.map(cf => {
            const cfDate = parseCashflowDate(cf.date);
            const isPast = !!cfDate && cfDate < now;
            const principal = Number(cf.principal ?? 0);
            const interest = Number(cf.interest ?? 0);
            const fees = Number(cf.fees ?? 0);
            const total = Number(cf.total ?? 0);
            const cumulative = Number(cf.cumulative ?? 0);

            return (
              <tr
                key={cf.date}
                className={`${isPast ? 'bg-gray-50 text-gray-500' : 'hover:bg-gray-50 text-gray-900'} transition-colors`}
              >
                <td
                  className={`px-4 py-3 font-medium ${isPast ? 'text-gray-600' : 'text-gray-900'}`}
                >
                  {formatCashflowDate(cf.date)}
                </td>
                <td className="px-4 py-3 text-right">{fmtCurrencyExact(principal)}</td>
                <td className="px-4 py-3 text-right">{fmtCurrencyExact(interest)}</td>
                <td className="px-4 py-3 text-right">{fmtCurrencyExact(fees)}</td>
                <td className="px-4 py-3 text-right font-medium">{fmtCurrencyExact(total)}</td>
                <td className="px-4 py-3 text-right font-medium">{fmtCurrencyExact(cumulative)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface YieldSummaryProps {
  loanId: string;
}

function YieldSummary({ loanId }: YieldSummaryProps) {
  const { data: yield_, isLoading, error } = useLoanYield(loanId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="lg" className="w-5 h-5 text-gray-400 animate-spin mr-2" />
        <span className="text-sm text-gray-400">Loading yield…</span>
      </div>
    );
  }

  if (error || !yield_) {
    return (
      <div className="flex items-center justify-center py-8 text-red-600">
        <AlertCircle className="w-5 h-5 mr-2" />
        <span className="text-sm">Error loading yield</span>
      </div>
    );
  }

  const safeYield = {
    allInYield: Number(yield_.allInYield ?? 0),
    fundingCost: Number(yield_.fundingCost ?? 0),
    nim: Number(yield_.nim ?? 0),
  };

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">All-in Yield</p>
        <p className="text-xl font-semibold text-gray-900">
          {formatAnnualPercent(safeYield.allInYield)}
        </p>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">Funding Cost</p>
        <p className="text-xl font-semibold text-gray-900">
          {formatAnnualPercent(safeYield.fundingCost)}
        </p>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">NIM</p>
        <p className="text-xl font-semibold text-green-600">{formatAnnualBps(safeYield.nim)}</p>
      </div>
    </div>
  );
}

interface CashflowsTabProps {
  loanId: string;
}

export function CashflowsTab({ loanId }: CashflowsTabProps) {
  return (
    <div className="space-y-6">
      {/* NPV Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">Net Present Value</h3>
        </div>
        <NPVSummary loanId={loanId} />
      </div>

      {/* Cashflow Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Monthly Cashflows</h3>
        <CashflowTable loanId={loanId} />
      </div>

      {/* Yield Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">Yield Summary</h3>
        </div>
        <YieldSummary loanId={loanId} />
      </div>
    </div>
  );
}
